import { EngineInvariantError } from "../errors";
import {
  applyDamageToCannon,
  canUnitDamageCannon,
  cannonAt,
  computeDamageToCannon,
} from "./cannon";
import {
  applyCounterAttackIfEligible,
  applyPrimaryAttackHit,
  markAttackerResolved,
} from "./combatResolve";
import { computeDamageFromRoll } from "./damage";
import { findUnit, getTile, manhattan } from "./grid";
import { unitTotalHp } from "./stacks";
import type { Coord } from "./types";
import type { GameEvent, GameState } from "./types";
import { canUnitDamage, effectiveAttackKind, isInAttackRange, isIndirectFire } from "./units";
import { countOwnedMonuments } from "./victory";

export { computeDamageFromRoll } from "./damage";

export function forecastDamage(
  state: GameState,
  { attackerId, targetId }: { attackerId: string; targetId: string },
): number {
  const attacker = findUnit(state, attackerId);
  const target = findUnit(state, targetId);
  const tile = getTile(state, target.pos);
  if (!canUnitDamage({ unit: attacker, defenderKind: target.kind })) {
    throw new EngineInvariantError(`Attacker cannot damage defender kind ${target.kind}`);
  }
  return computeDamageFromRoll({
    attacker,
    defender: target,
    defenderTile: tile,
    attackerMonumentCount: countOwnedMonuments(state.tiles, attacker.owner),
  });
}

export function forecastDamageToCannon(
  state: GameState,
  { attackerId, targetPos }: { attackerId: string; targetPos: Coord },
): number {
  const attacker = findUnit(state, attackerId);
  if (!cannonAt(state, targetPos)) {
    throw new EngineInvariantError(`No cannon at (${targetPos.x}, ${targetPos.y})`);
  }
  if (!canUnitDamageCannon(attacker)) {
    throw new EngineInvariantError(`Attacker cannot damage cannon`);
  }
  return computeDamageToCannon({
    attacker,
    attackerMonumentCount: countOwnedMonuments(state.tiles, attacker.owner),
  });
}

export function forecastCounterDamage({
  state,
  attackerId,
  targetId,
  attackerDest,
  attackDamage,
}: {
  state: GameState;
  attackerId: string;
  targetId: string;
  attackerDest: Coord;
  attackDamage: number;
}): number | null {
  const attacker = findUnit(state, attackerId);
  const target = findUnit(state, targetId);
  const targetHp = unitTotalHp(target);
  const bothDirectFire =
    !isIndirectFire(effectiveAttackKind(attacker)) && !isIndirectFire(effectiveAttackKind(target));
  if (
    attackDamage >= targetHp ||
    !bothDirectFire ||
    !canUnitDamage({ unit: target, defenderKind: effectiveAttackKind(attacker) })
  ) {
    return null;
  }
  if (!isInAttackRange(target.kind, manhattan(target.pos, attackerDest))) {
    return null;
  }

  const stateAfterAttack: GameState = {
    ...state,
    units: state.units.map((u) => {
      if (u.id === attacker.id) return { ...u, pos: attackerDest };
      if (u.id === target.id) return { ...u, hp: targetHp - attackDamage };
      return u;
    }),
  };
  return forecastDamage(stateAfterAttack, { attackerId: target.id, targetId: attacker.id });
}

export function resolveAttack(
  state: GameState,
  { attackerId, targetId }: { attackerId: string; targetId: string },
): { state: GameState; events: GameEvent[] } {
  const attacker = findUnit(state, attackerId);
  const target = findUnit(state, targetId);
  if (!canUnitDamage({ unit: attacker, defenderKind: target.kind })) {
    throw new EngineInvariantError(`Attacker cannot damage defender kind ${target.kind}`);
  }
  const events: GameEvent[] = [];
  const damage = computeDamageFromRoll({
    attacker,
    defender: target,
    defenderTile: getTile(state, target.pos),
    attackerMonumentCount: countOwnedMonuments(state.tiles, attacker.owner),
  });
  const { next: afterHit, killed } = applyPrimaryAttackHit({
    state,
    attacker,
    target,
    damage,
    events,
  });
  let next = markAttackerResolved({ state: afterHit, attacker, killed });
  events.push({
    playerId: state.currentPlayer,
    type: "attacked",
    attackerId,
    attackerKind: effectiveAttackKind(attacker),
    targetId,
    damage,
    killed,
    targetKind: target.kind,
  });
  if (!killed) {
    next = applyCounterAttackIfEligible({ next, attackerId, targetId, events });
  }
  return { state: next, events };
}

export function resolveAttackOnCannon(
  state: GameState,
  { attackerId, targetPos }: { attackerId: string; targetPos: Coord },
): { state: GameState; events: GameEvent[] } {
  const attacker = findUnit(state, attackerId);
  if (!cannonAt(state, targetPos)) {
    throw new EngineInvariantError(`No cannon at (${targetPos.x}, ${targetPos.y})`);
  }
  if (!canUnitDamageCannon(attacker)) {
    throw new EngineInvariantError(`Attacker cannot damage cannon`);
  }
  const damage = computeDamageToCannon({
    attacker,
    attackerMonumentCount: countOwnedMonuments(state.tiles, attacker.owner),
  });
  const { state: afterHit, destroyed } = applyDamageToCannon({
    state,
    pos: targetPos,
    damage,
  });
  const next = markAttackerResolved({ state: afterHit, attacker, killed: false });
  const events: GameEvent[] = [
    {
      playerId: state.currentPlayer,
      type: "attacked",
      attackerId,
      attackerKind: effectiveAttackKind(attacker),
      targetPos,
      damage,
      killed: destroyed,
      targetKind: "cannon",
    },
  ];
  return { state: next, events };
}
